import type { IUseCase } from '@/shared/contracts/use-case.contract';
import { HttpMethod, type IHttpClient } from '@/shared/contracts/http-client.contract';

import {
  ENDPOINT,
  STORE_REVIEWS_PER_PAGE,
  type GetStoreReviewsRequest,
  type GetStoreReviewsResponse,
} from '../types/get-store-reviews';

/**
 * PRAT-44: a API antiga não manda `reviews_enabled`, `hidden_comments_count`
 * nem `comment_hidden`; assume avaliações ligadas e nada ocultado.
 */
export function normalizeStoreReviewsResponse(
  response: Partial<GetStoreReviewsResponse>
): GetStoreReviewsResponse {
  const reviews = (response.reviews ?? []).map(review => ({
    ...review,
    comment_hidden: review.comment_hidden ?? false,
    comment_hidden_reason: review.comment_hidden_reason ?? null,
  }));

  return {
    reviews_enabled: response.reviews_enabled ?? true,
    hidden_comments_count: response.hidden_comments_count ?? 0,
    summary: response.summary ?? null,
    reviews,
    pagination: response.pagination ?? {
      current_page: 1,
      last_page: 1,
      per_page: STORE_REVIEWS_PER_PAGE,
      total: reviews.length,
    },
  };
}

type UseCaseInput = GetStoreReviewsRequest;
type UseCaseOutput = GetStoreReviewsResponse;

/**
 * Lista, paginada, as avaliações de uma loja (mais recentes primeiro), com o
 * resumo da nota dos últimos 30 dias. Usado na lista de avaliações da página
 * da loja.
 */
export class GetStoreReviewsUseCase implements IUseCase<UseCaseInput, UseCaseOutput> {
  private client: IHttpClient;

  constructor(client: IHttpClient) {
    this.client = client;
  }

  async execute(input: UseCaseInput): Promise<UseCaseOutput> {
    const { slug, page = 1, per_page = STORE_REVIEWS_PER_PAGE } = input;

    if (!slug) {
      throw new Error('Slug is required');
    }

    const queryParams = new URLSearchParams({
      page: String(page),
      per_page: String(per_page),
    });

    const response = await this.client.sendRequest<Partial<UseCaseOutput>, never>({
      endpoint: `${ENDPOINT(slug)}?${queryParams.toString()}`,
      method: HttpMethod.GET,
    });

    return normalizeStoreReviewsResponse(response);
  }
}
